import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Line,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { CheckRecord, Category } from '@shared/types';
import { CATEGORY_LABELS } from '@shared/types';
import { listUrlChecks } from '../api';
import { formatDate } from '../scoreUtils';

const CATEGORY_COLORS: Record<Category, string> = {
  technical: '#2563eb',
  cwv: '#16a34a',
  content: '#db2777',
  structured: '#9333ea',
  media: '#ea580c',
  risk: '#0891b2',
};

const CATEGORY_ORDER: Category[] = [
  'technical',
  'cwv',
  'content',
  'structured',
  'media',
  'risk',
];

interface Point {
  runAt: string;
  label: string;
  total: number | null;
  technical?: number;
  cwv?: number;
  content?: number;
  structured?: number;
  media?: number;
  risk?: number;
}

export default function HistoryPage() {
  const { urlId } = useParams<{ urlId: string }>();
  const [records, setRecords] = useState<CheckRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);

  useEffect(() => {
    if (!urlId) return;
    (async () => {
      try {
        const r = await listUrlChecks(urlId);
        setRecords(r);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [urlId]);

  const data: Point[] = useMemo(() => {
    return records
      .filter((r) => r.status === 'done' && r.totalScore != null)
      .slice()
      .sort((a, b) => (a.runAt < b.runAt ? -1 : 1))
      .map((r) => {
        const p: Point = {
          runAt: r.runAt,
          label: formatDate(r.runAt),
          total: r.totalScore,
        };
        for (const c of r.categoryScores ?? []) {
          p[c.category] = c.score;
        }
        return p;
      });
  }, [records]);

  if (loading) return <div className="loading">読み込み中…</div>;

  return (
    <div className="page">
      <div className="page-head">
        <h1>スコア推移</h1>
        <Link className="btn" to="/admin">
          ← サイト一覧
        </Link>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      {data.length === 0 ? (
        <p className="muted">完了済みのチェック履歴がまだありません。</p>
      ) : (
        <>
          <label className="check-form__label toggle-line">
            <input
              type="checkbox"
              checked={showCategories}
              onChange={(e) => setShowCategories(e.target.checked)}
            />
            カテゴリ別も表示
          </label>

          <div className="card">
            <ResponsiveContainer width="100%" height={380}>
              <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="総合"
                  stroke="#111827"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
                {showCategories &&
                  CATEGORY_ORDER.map((c) => (
                    <Line
                      key={c}
                      type="monotone"
                      dataKey={c}
                      name={CATEGORY_LABELS[c]}
                      stroke={CATEGORY_COLORS[c]}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h2>チェック履歴</h2>
            <table className="history-table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>総合</th>
                  {CATEGORY_ORDER.map((c) => (
                    <th key={c}>{CATEGORY_LABELS[c]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data].reverse().map((p) => (
                  <tr key={p.runAt}>
                    <td className="small">{p.label}</td>
                    <td>
                      <strong>{p.total}</strong>
                    </td>
                    {CATEGORY_ORDER.map((c) => (
                      <td key={c} className="small">
                        {p[c] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
